import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Building2, User, Mail, Calendar, Clock, FileText, Download, RefreshCw, Loader2, ExternalLink, ArrowRightCircle, Globe, Zap, FolderOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPE_LABELS = {
  website: "Website", booking: "Booking System", crm: "CRM", admin: "Admin System",
  calculator: "Calculator Tool", ecommerce: "E-commerce", automation: "Automation", custom: "Custom",
};

export default function OnboardingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState("overview");
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/onboarding-sessions/${id}`, { withCredentials: true })
      .then(r => setSession(r.data))
      .catch(() => navigate("/admin/onboarding"));
  }, [id, navigate]);

  const regenerateBrief = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/onboarding-sessions/${id}/regenerate-brief`, {}, { withCredentials: true });
      setSession(prev => ({ ...prev, ai_brief: data.brief }));
    } catch (e) { alert("Failed to regenerate brief"); }
    setRegenerating(false);
  };

  const convertToCustomer = async () => {
    if (!window.confirm("Convert this onboarding to a customer?")) return;
    try {
      await axios.post(`${API}/admin/customers`, {
        company_name: session.company_name, contact_name: session.contact_name,
        email: session.email, status: "active",
      }, { withCredentials: true });
      alert("Customer created!");
      navigate("/admin/customers");
    } catch (e) { alert("Failed to create customer"); }
  };

  if (!session) return <div className="text-[#857973]">Loading...</div>;

  const a = session.answers || {};
  const tabs = [
    { key: "overview", label: "Overview", icon: Building2 },
    { key: "details", label: "Project Details", icon: FileText },
    { key: "brief", label: "AI Brief", icon: Zap },
    { key: "files", label: `Files (${(session.files?.length || 0) + (session.dropbox_files?.length || 0)})`, icon: FolderOpen },
    { key: "meeting", label: "Meeting", icon: Calendar },
  ];

  return (
    <div data-testid="onboarding-detail">
      <button onClick={() => navigate("/admin/onboarding")} className="flex items-center gap-2 text-sm text-[#857973] hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Sessions
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-outfit font-bold text-2xl text-white">{session.company_name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${session.completed ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
              {session.completed ? "Completed" : `Step ${session.current_step}`}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#857973]">
            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {session.contact_name}</span>
            <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {session.email}</span>
            <span>{session.created_at?.slice(0, 10)}</span>
          </div>
        </div>
        <button onClick={convertToCustomer} data-testid="convert-to-customer-btn"
          className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
          <ArrowRightCircle className="w-4 h-4" /> Convert to Customer
        </button>
      </div>

      <div className="flex gap-1 border-b border-[#1E3050] mb-6 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition-colors border-b-2 ${tab === key ? "border-[#F59073] text-[#F59073]" : "border-transparent text-[#857973] hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoCard title="COMPANY">
            <InfoRow label="Company" value={session.company_name} />
            <InfoRow label="Contact" value={session.contact_name} />
            <InfoRow label="Email" value={session.email} />
            <InfoRow label="Industry" value={a.industry} />
            <InfoRow label="Size" value={a.company_size} />
            <InfoRow label="Website" value={a.current_website} link />
          </InfoCard>
          <InfoCard title="PROJECT">
            <InfoRow label="Types" value={a.project_types?.map(t => PROJECT_TYPE_LABELS[t] || t).join(", ")} />
            <InfoRow label="Timeline" value={a.timeline} />
            <InfoRow label="Budget" value={a.budget} />
            <InfoRow label="Priority" value={a.priority} />
            <InfoRow label="Design" value={a.design_style} />
          </InfoCard>
          <InfoCard title="GOALS" full>
            <p className="text-sm text-neutral-300 font-manrope leading-relaxed">{a.goals || "—"}</p>
          </InfoCard>
          <InfoCard title="PAIN POINTS" full>
            <p className="text-sm text-neutral-300 font-manrope leading-relaxed">{a.pain_points || "—"}</p>
          </InfoCard>
          {a.business_description && (
            <InfoCard title="BUSINESS DESCRIPTION" full>
              <p className="text-sm text-neutral-300 font-manrope leading-relaxed">{a.business_description}</p>
            </InfoCard>
          )}
        </div>
      )}

      {/* Project Details Tab */}
      {tab === "details" && (
        <div className="space-y-6">
          {a.project_types?.map(pt => {
            const label = PROJECT_TYPE_LABELS[pt] || pt;
            const keys = Object.keys(a).filter(k => k.startsWith(pt.split("_")[0]) || k.startsWith(pt));
            return (
              <InfoCard key={pt} title={label.toUpperCase()}>
                {keys.length > 0 ? keys.map(k => (
                  <InfoRow key={k} label={k.replace(/_/g, " ").replace(pt, "").trim()} value={a[k]} />
                )) : <p className="text-sm text-[#857973]">No specific details provided</p>}
              </InfoCard>
            );
          })}
          <InfoCard title="DESIGN & BRAND">
            <InfoRow label="Style" value={a.design_style} />
            <InfoRow label="Colors" value={a.colors} />
            <InfoRow label="Tone" value={a.brand_tone} />
            <InfoRow label="References" value={a.reference_sites} />
          </InfoCard>
          <InfoCard title="FEATURES & INTEGRATIONS">
            <InfoRow label="Features" value={a.specific_features} />
            <InfoRow label="Integrations" value={a.integrations} />
            <InfoRow label="Technical" value={a.technical_requirements} />
          </InfoCard>
          <InfoCard title="CURRENT SOLUTIONS">
            <p className="text-sm text-neutral-300 font-manrope leading-relaxed">{a.current_solutions || "—"}</p>
          </InfoCard>
        </div>
      )}

      {/* AI Brief Tab */}
      {tab === "brief" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-medium text-white">AI-Generated Project Brief</h3>
            <button onClick={regenerateBrief} disabled={regenerating} data-testid="regenerate-brief-btn"
              className="flex items-center gap-2 text-sm text-[#F59073] hover:text-white transition-colors disabled:opacity-50">
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {regenerating ? "Generating..." : "Regenerate Brief"}
            </button>
          </div>
          {session.ai_brief ? (
            <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6 md:p-8" data-testid="ai-brief-content">
              <div className="prose prose-sm prose-invert max-w-none font-manrope">
                {session.ai_brief.split("\n").map((line, i) => {
                  if (line.startsWith("# ")) return <h1 key={i} className="font-outfit font-bold text-xl text-white mt-6 mb-3">{line.slice(2)}</h1>;
                  if (line.startsWith("## ")) return <h2 key={i} className="font-outfit font-bold text-lg text-[#F59073] mt-6 mb-2">{line.slice(3)}</h2>;
                  if (line.startsWith("### ")) return <h3 key={i} className="font-outfit font-medium text-base text-white mt-4 mb-2">{line.slice(4)}</h3>;
                  if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="text-neutral-300 ml-4 mb-1">{line.slice(2)}</li>;
                  if (line.startsWith("**") && line.endsWith("**")) return <p key={i} className="text-white font-medium mt-2">{line.replace(/\*\*/g, "")}</p>;
                  if (line.trim() === "") return <div key={i} className="h-2" />;
                  return <p key={i} className="text-[#857973] leading-relaxed mb-1">{line}</p>;
                })}
              </div>
            </div>
          ) : (
            <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
              <Zap className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
              <p className="text-[#857973] mb-4">No AI brief generated yet</p>
              <button onClick={regenerateBrief} disabled={regenerating}
                className="bg-[#F59073] text-black px-6 py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
                {regenerating ? "Generating..." : "Generate Brief"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Files Tab */}
      {tab === "files" && (
        <div className="space-y-6">
          {/* Uploaded files (MongoDB) */}
          {session.files?.length > 0 && (
            <div>
              <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-3">UPLOADED FILES</h3>
              <div className="space-y-2">
                {session.files.map(f => (
                  <div key={f.id} className="flex items-center justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-[#F59073]" />
                      <div>
                        <span className="text-sm text-white">{f.filename}</span>
                        <span className="text-xs text-[#857973] block">{(f.size / 1024).toFixed(1)} KB &middot; {f.content_type} &middot; {f.uploaded_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                    <a href={`${API}/admin/onboarding-sessions/${id}/file/${f.id}`} target="_blank" rel="noreferrer"
                      className="text-[#F59073] hover:text-white transition-colors" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dropbox files */}
          {session.dropbox_files?.length > 0 && (
            <div>
              <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-3">DROPBOX FILES</h3>
              <div className="space-y-2">
                {session.dropbox_files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
                    <div className="flex items-center gap-3">
                      <FolderOpen className="w-4 h-4 text-blue-400" />
                      <div>
                        <span className="text-sm text-white">{f.name}</span>
                        <span className="text-xs text-[#857973] block">{f.path} {f.size ? `· ${(f.size / 1024).toFixed(1)} KB` : ""}</span>
                      </div>
                    </div>
                    {f.url && (
                      <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-white transition-colors" title="Open in Dropbox">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!session.files?.length && !session.dropbox_files?.length) && (
            <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
              <FolderOpen className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
              <p className="text-[#857973]">No files uploaded</p>
            </div>
          )}
        </div>
      )}

      {/* Meeting Tab */}
      {tab === "meeting" && (
        <div>
          {session.meeting ? (
            <InfoCard title="MEETING REQUEST">
              <InfoRow label="Preferred dates" value={session.meeting.preferred_dates?.filter(Boolean).join(", ") || "—"} />
              <InfoRow label="Preferred time" value={session.meeting.preferred_time} />
              <InfoRow label="Notes" value={session.meeting.notes} />
              <InfoRow label="Booked at" value={session.meeting.booked_at?.slice(0, 16).replace("T", " ")} />
            </InfoCard>
          ) : (
            <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-12 text-center">
              <Calendar className="w-8 h-8 text-[#1E3050] mx-auto mb-3" />
              <p className="text-[#857973]">No meeting booked yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoCard({ title, children, full }) {
  return (
    <div className={`bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6 ${full ? "md:col-span-2" : ""}`}>
      <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-4">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, link }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-[#1E3050] last:border-0">
      <span className="text-xs text-[#857973] capitalize">{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-sm text-[#F59073] hover:underline max-w-[60%] text-right truncate">{value}</a>
      ) : (
        <span className="text-sm text-white text-right max-w-[60%]">{value}</span>
      )}
    </div>
  );
}
